/**
 * In-app links: `aiva://` URLs that an Actions-tab card follows inside the
 * task pane instead of opening a browser tab.
 *
 * A skill's Notify card compiles its Navigate / Open Email / Open Document
 * buttons into a `links` context block (SkillCompiler/notify_card_hitl.py).
 * Those point at places inside the add-in, so the card hands them to this
 * bridge and TaskPane brings the right tab forward — the same two-component
 * shape as the Deckorate nav-bridge.
 *
 *   aiva://module/<tab>?view=<view>&record=<ref>   → that module's tab, opened on the record
 *   aiva://email/<ref>                            → that message in Outlook (the Email tab without a ref)
 *   aiva://document/<type>/<ref>                  → that file in Office for the web (the Files tab without a ref)
 */

import { ALL_MODULES } from '../skills/registry';
import { BACKEND_MODULE_TILE_ALIASES, normaliseModuleName } from '../skills/backendModuleTile';

export const IN_APP_SCHEME = 'aiva://';
/** The More tab: where a module link lands when the add-in has no tab for it. */
export const LAUNCHER_TAB = 'modules';

export interface InAppNavTarget {
  /** The TaskPane tab id to bring forward. */
  readonly tab: string;
  readonly view?: string;
  readonly record?: string;
}

export function isInAppLink(url: string | null | undefined): boolean {
  return typeof url === 'string' && url.toLowerCase().startsWith(IN_APP_SCHEME);
}

/**
 * Parse an `aiva://` URL; null for anything else or a malformed one.
 *
 * The record is everything after its prefix, taken raw. The skill's agent
 * substitutes `{{variables}}` into these links verbatim (notify_card_hitl.py
 * asks it to, and an LLM cannot be relied on to percent-encode), so a record
 * can itself be a SharePoint URL with its own `?`, `&` and `//`. Splitting the
 * link on `?` or `/` first would cut such a record apart.
 */
export function parseInAppLink(url: string | null | undefined): InAppNavTarget | null {
  if (!isInAppLink(url)) return null;
  const rest = (url as string).slice(IN_APP_SCHEME.length);
  const kindEnd = rest.search(/[/?]/);
  const kind = kindEnd < 0 ? rest : rest.slice(0, kindEnd);
  const body = kindEnd < 0 ? '' : rest.slice(kindEnd + 1);

  if (kind === 'module') return parseModuleLink(body);
  if (kind === 'email') return navTarget('email', '', decodeRecord(body));
  if (kind === 'document') return parseDocumentLink(body);
  return null;
}

/** `<type>/<ref>` — the document type is the view. */
function parseDocumentLink(body: string): InAppNavTarget {
  const typeEnd = body.indexOf('/');
  const view = decodeURIComponentSafe(typeEnd < 0 ? body : body.slice(0, typeEnd));
  const record = typeEnd < 0 ? '' : decodeRecord(body.slice(typeEnd + 1));
  return navTarget('files', view, record);
}

/** A target that omits an empty view or record rather than carrying `''`. */
function navTarget(tab: string, view: string, record: string): InAppNavTarget {
  return { tab, ...(view ? { view } : {}), ...(record ? { record } : {}) };
}

/** `<tab>?view=<view>&record=<ref>` — the compiler always writes `record` last. */
function parseModuleLink(body: string): InAppNavTarget | null {
  const queryAt = body.indexOf('?');
  const tab = decodeURIComponentSafe((queryAt < 0 ? body : body.slice(0, queryAt)).split('/')[0] ?? '');
  if (!tab) return null;
  const query = queryAt < 0 ? '' : body.slice(queryAt + 1);
  const recordMatch = /(?:^|&)record=/.exec(query);
  const record = recordMatch ? decodeRecord(query.slice(recordMatch.index + recordMatch[0].length)) : '';
  const view = new URLSearchParams(recordMatch ? query.slice(0, recordMatch.index) : query).get('view') ?? '';
  return navTarget(tab.toLowerCase(), view, record);
}

/**
 * A web URL is kept byte-for-byte: SharePoint paths legitimately carry `%20`
 * and `%2F`, and decoding them would point at a different (or no) file. Any
 * other record is decoded in case the agent did encode it.
 */
function decodeRecord(raw: string): string {
  const trimmed = raw.trim();
  return /^https?:\/\//i.test(trimmed) ? trimmed : decodeURIComponentSafe(trimmed);
}

function decodeURIComponentSafe(part: string): string {
  try {
    return decodeURIComponent(part);
  } catch {
    return part;
  }
}

/** The task pane tab a module link should open: the tile whose id or
 *  Studio name matches the slug, else the launcher. Resolved at click time so a
 *  module added to the catalogue needs no compiler change. */
export function resolveInAppTab(target: InAppNavTarget): string {
  const slug = normaliseModuleName(target.tab);
  if (!slug) return LAUNCHER_TAB;
  const aliased = BACKEND_MODULE_TILE_ALIASES[slug];
  if (aliased) return aliased;
  const tile = ALL_MODULES.find((m) => m.id === slug || normaliseModuleName(m.name) === slug);
  return tile ? tile.id : LAUNCHER_TAB;
}

type NavListener = (target: InAppNavTarget) => void;
const listeners = new Set<NavListener>();

/**
 * A request that carries a record, parked for the module that opens it.
 *
 * Opening a record takes two components: TaskPane brings the tab forward, and
 * the module (Knowledge, MyQueue, Engage) opens the record. The module is
 * usually not mounted when the request fires, so it claims the park on mount —
 * the same shape as the Deckorate nav-bridge. Only the newest request
 * survives, and a record-less request clears it, so a stale record can never
 * hijack a later, ordinary open of the module.
 */
let pending: InAppNavTarget | null = null;

/** Subscribe to in-app navigation requests. Returns an unsubscribe for effect cleanup. */
export function subscribeInAppNav(listener: NavListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Ask the task pane to navigate. Safe to call from any component. */
export function requestInAppNav(target: InAppNavTarget): void {
  pending = target.record ? target : null;
  for (const listener of listeners) listener(target);
}

/**
 * Read and clear the parked record request for `tab`; null when the park is
 * empty or belongs to another tab (which is left in place for its own module).
 */
export function consumePendingInAppNav(tab: string): InAppNavTarget | null {
  if (pending?.tab !== tab) return null;
  const target = pending;
  pending = null;
  return target;
}
